import * as React from 'react';
import { ContentState } from 'draft-js';
import { betweenOrEqual, secondsToString } from '../utils';
import { SegmentWordInfo, RegionInfo, WordInfo, BlockInfo } from '../models';
import { SpeakerInfo } from '@gen/proto/lr/transcript/v1alpha1/transcript_pb';
import './Segment.css';

/* Missing type definition */
/* type DraftDecoratorComponentProps = {
 *   blockKey: BlockNodeKey,
 *   children?: Array<React.Node>,
 *   contentState: ContentState,
 *   decoratedText: string,
 *   dir: ?HTMLDir,
 *   end: number,
 *
 *   // Many folks mistakenly assume that there will always be an 'entityKey'
 *   // passed to a DecoratorComponent.
 *   // To find the `entityKey`, Draft calls
 *   // `contentBlock.getEntityKeyAt(leafNode)` and in many cases the leafNode does
 *   // not have an entityKey. In those cases the entityKey will be null or
 *   // undefined. That's why `getEntityKeyAt()` is typed to return `?string`.
 *   // See https://github.com/facebook/draft-js/blob/2da3dcb1c4c106d1b2a0f07b3d0275b8d724e777/src/model/immutable/BlockNode.js#L51
 *   entityKey: ?string,
 *
 *   key: React.Key,
 *   offsetKey: string,
 *   start: number,
 * };
 *  *
 */

type DecoratorSegmentProps = {
  children?: React.ReactNode;
  playerRef: React.RefObject<HTMLMediaElement>;
  onEditSpeaker: (key: string, id?: string) => void;
  getSpeakersMap: () => [string, SpeakerInfo.AsObject][];
  entityKey: string;
  contentState: ContentState;
  blockKey: string;
  editable: boolean;
  setWaveformContainer: (component: HTMLDivElement, regions: RegionInfo[], blockKey: string) => void;
};

export const DecoratorSegment: React.FunctionComponent<DecoratorSegmentProps> = ({
  blockKey,
  children,
  contentState,
  editable,
  entityKey,
  playerRef,
  onEditSpeaker,
  getSpeakersMap,
  setWaveformContainer,
}) => {
  const [active, setActive] = React.useState<boolean>(false);

  const maybeSetTime = (time: number) => {
    if (playerRef.current) {
      playerRef.current.currentTime = time;
    }
  };

  const contentBlock = contentState.getBlockForKey(blockKey);
  const { speakerId, startTime, endTime } = contentBlock.getData().toObject() as BlockInfo;

  let speakerFullName;
  if (speakerId) {
    const speakersMap = getSpeakersMap();
    const speaker = speakersMap.find(([key, _]) => key === speakerId);
    speakerFullName = speaker ? speaker[1].fullName : '';
  }

  const entity = contentState.getEntity(entityKey);
  const { data } = entity.getData() as { data: SegmentWordInfo[] };

  React.useEffect(() => {
    const handleTimeUpdate = (event: Event) => {
      const { currentTime } = event.target as HTMLAudioElement;
      const active = data.reduce((active: boolean, data: WordInfo) => {
        const { startTime, endTime } = data;
        return active || betweenOrEqual(startTime, currentTime, endTime);
      }, false);

      setActive(active);
    };

    if (playerRef.current) {
      playerRef.current.addEventListener('timeupdate', handleTimeUpdate);
    }

    return function cleanup() {
      if (playerRef.current) {
        playerRef.current.removeEventListener('timeupdate', handleTimeUpdate);
      }
    };
  }, []);

  const handleClick = () => {
    const selection = window.getSelection();
    if (selection) {
      const offset = selection.anchorOffset;
      if (selection.isCollapsed) {
        handleOffset(offset);
      }
    }
  };

  const waveformRef = React.useRef<HTMLDivElement>(null);

  const handleWaveform = () => {
    if (waveformRef.current) {
      const lastBlock = contentState.getBlockBefore(blockKey);
      const nextBlock = contentState.getBlockAfter(blockKey);
      const lastBlockData = lastBlock ? lastBlock.getData().toObject() : undefined;
      const nextBlockData = nextBlock ? nextBlock.getData().toObject() : undefined;
      const regions = [
        { start: startTime, end: endTime, color: '#19659F45', loop: true, resize: true },
      ] as RegionInfo[];
      if (lastBlockData) {
        regions.unshift({ start: lastBlockData.startTime, end: lastBlockData.endTime });
      }
      if (nextBlockData) {
        regions.push({ start: nextBlockData.startTime, end: nextBlockData.endTime });
      }
      setWaveformContainer(waveformRef.current, regions, blockKey);
    }
  };

  const handleOffset = (offset: number) => {
    const text = contentBlock.getText();
    const index = text.split('').reduce((index: number, char: string, i: number) => {
      const newIndex = i >= offset ? index : i === 0 ? index : char === ' ' ? index + 1 : index;
      return newIndex;
    }, 0);

    try {
      const word = data[index];
      if (word.startTime > 0) {
        maybeSetTime(word.startTime);
      }
    } catch (error) {
      console.debug('set time failed');
    }
  };

  const handleEdit = () => {
    if (editable) {
      onEditSpeaker(blockKey, speakerId);
    }
  };

  return (
    <React.Fragment>
      <span contentEditable={false} className='segment-speaker'>
        <span className={`name ${!speakerFullName ? 'anonymous' : ''}`} onClick={handleEdit}>
          {speakerFullName || 'Ónefndur'}
        </span>
        <span className={'timestamp'} onClick={handleWaveform}>
          {`${secondsToString(startTime)} - ${secondsToString(endTime)}`}
        </span>
      </span>
      <span onClick={handleClick} className={`segment ${active ? 'active-segment' : ''}`}>
        {children}
      </span>
      <div className={'segment-waveform-container'} contentEditable={false} ref={waveformRef} />
    </React.Fragment>
  );
};

export default React.memo(DecoratorSegment);
